import { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Checkbox,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ChevronRight,
  ChevronLeft,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
  ArrowUpward,
  ArrowDownward,
  DragIndicator,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ReportBuilderValues, ReportsBuilderContext } from '../context/ReportsBuilderContext';
import { theme } from '../../../theme';

const FieldSelector = () => {
  const {
    t,
    selectedDataGroup,
    columnMetrics,
    columns,
    removeColumn,
    loading,
    report,
    setReport,
    setColumns,
    markAsChanged,
  } = useContext(ReportsBuilderContext) as ReportBuilderValues;

  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedFields, setSelectedFields] = useState<number[]>([]);
  const [orderedSelectedFields, setOrderedSelectedFields] = useState<number[]>([]);

  // Get currently selected metric IDs
  const selectedMetricIds = report?.reportColumns?.map((col) => col.metricId).filter((id) => id !== 0) ?? [];

  // Get available metrics (not selected, alphabetically sorted)
  const availableMetrics = columnMetrics
    ?.filter((metric) => !selectedMetricIds.includes(metric.id!))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '')) ?? [];


  // Update ordered selected fields when columns change
  useEffect(() => {
    const ordered = columns
      .map((col) => col.metricId)
      .filter((id): id is number => id !== undefined && id !== 0);
    setOrderedSelectedFields(ordered);
  }, [columns]);

  const handleToggleAvailable = (metricId: number) => {
    setSelectedAvailable((prev) =>
      prev.includes(metricId)
        ? prev.filter((id) => id !== metricId)
        : [...prev, metricId],
    );
  };

  const handleToggleSelected = (metricId: number) => {
    setSelectedFields((prev) =>
      prev.includes(metricId)
        ? prev.filter((id) => id !== metricId)
        : [...prev, metricId],
    );
  };

  const handleAddSelected = () => {
    if (selectedAvailable.length === 0) return;
    
    // Get current non-empty columns
    const updatedColumns = [...columns.filter((col) => col.metricId !== 0)];
    
    // Add all selected metrics
    selectedAvailable.forEach((metricId) => {
      updatedColumns.push({
        id: updatedColumns.length + 1,
        reportId: 0,
        metricId: metricId,
      });
    });
    
    // Add empty column at the end
    updatedColumns.push({ id: updatedColumns.length + 1, reportId: 0, metricId: 0 });
    
    // Update both columns state and report
    setColumns(updatedColumns);
    setReport((prev) => ({
      ...prev!,
      reportColumns: updatedColumns,
    }));
    
    markAsChanged();
    setSelectedAvailable([]);
  };

  const handleAddAll = () => {
    if (!availableMetrics || availableMetrics.length === 0) return;
    
    // Get current non-empty columns
    const updatedColumns = [...columns.filter((col) => col.metricId !== 0)];
    
    // Add all available metrics
    availableMetrics.forEach((metric) => {
      updatedColumns.push({
        id: updatedColumns.length + 1,
        reportId: 0,
        metricId: metric.id!,
      });
    });
    
    // Add empty column at the end
    updatedColumns.push({ id: updatedColumns.length + 1, reportId: 0, metricId: 0 });
    
    // Update both columns state and report
    setColumns(updatedColumns);
    setReport((prev) => ({
      ...prev!,
      reportColumns: updatedColumns,
    }));
    
    markAsChanged();
    setSelectedAvailable([]);
  };

  const handleRemoveSelected = () => {
    if (selectedFields.length === 0) return;
    
    // Remove in reverse order to avoid index shifting issues
    const sortedIndices = selectedFields
      .map((metricId) => columns.findIndex((col) => col.metricId === metricId))
      .filter((index) => index !== -1)
      .sort((a, b) => b - a); // Sort descending
    
    sortedIndices.forEach((columnIndex) => {
      removeColumn(columnIndex);
    });
    setSelectedFields([]);
  };

  const handleRemoveAll = () => {
    // Get all non-empty column indices in descending order
    const nonEmptyIndices = columns
      .map((col, index) => (col.metricId !== 0 ? index : -1))
      .filter((index) => index !== -1)
      .sort((a, b) => b - a); // Sort descending to avoid index shifting
    
    // Remove all columns from end to beginning
    nonEmptyIndices.forEach((columnIndex) => {
      removeColumn(columnIndex);
    });
    setSelectedFields([]);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    
    const items = Array.from(orderedSelectedFields);
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    setOrderedSelectedFields(items);
    updateColumnsOrder(items);
  };

  const handleMoveDown = (index: number) => {
    if (index === orderedSelectedFields.length - 1) return;
    
    const items = Array.from(orderedSelectedFields);
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    setOrderedSelectedFields(items);
    updateColumnsOrder(items);
  };

  const updateColumnsOrder = (items: number[]) => {
    // Build new columns array based on the reordered metric IDs
    const newColumns = items.map((metricId, index) => {
      const existingColumn = columns.find((col) => col.metricId === metricId);
      return {
        ...existingColumn!,
        id: index + 1,
      };
    });
    
    // Add empty column at the end
    const emptyColumn = columns.find((col) => col.metricId === 0);
    if (emptyColumn) {
      newColumns.push({ ...emptyColumn, id: newColumns.length + 1 });
    }

    // Update only the report - the columns will be synced through the component re-render
    setReport((prev) => ({
      ...prev!,
      reportColumns: newColumns,
    }));
  };

  // Drag and drop handler for react-beautiful-dnd
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = Array.from(orderedSelectedFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedSelectedFields(items);
    updateColumnsOrder(items);
  };

  if (loading) {
    return null;
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderRadius: '4px',
        borderColor: theme.palette.custom.gray[200],
        p: 3,
        mt: 3,
        opacity: selectedDataGroup ? 1 : 0.5,
        pointerEvents: selectedDataGroup ? 'auto' : 'none',
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: 'bold',
        }}
      >
        {t('columns')} *
      </Typography>

      <Grid
        container
        spacing={2}
      >
        {/* Available Fields */}
        <Grid
          item
          xs={12}
          md={5}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.custom.gray[200],
              height: 400,
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: theme.palette.custom.gray[200],
                backgroundColor: theme.palette.custom.gray[50],
              }}
            >
              <Typography
                fontWeight="bold"
              >
                {t('availableFields')}
              </Typography>
            </Box>
            <List dense>
              {availableMetrics?.map((metric) => (
                <ListItem
                  key={metric.id}
                  disablePadding
                >
                  <ListItemButton
                    onClick={() => handleToggleAvailable(metric.id!)}
                    dense
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedAvailable.includes(metric.id!)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={metric.name}
                      title={metric.description || metric.name || ''}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Transfer Buttons */}
        <Grid
          item
          xs={12}
          md={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            variant="primary"
            size="lg"
            onClick={handleAddAll}
            disabled={!availableMetrics || availableMetrics.length === 0}
            fullWidth
            startIcon={<KeyboardDoubleArrowRight />}
          >
            {t('addAll')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleAddSelected}
            disabled={selectedAvailable.length === 0}
            fullWidth
            startIcon={<ChevronRight />}
          >
            {t('addSelected')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleRemoveSelected}
            disabled={selectedFields.length === 0}
            fullWidth
            startIcon={<ChevronLeft />}
          >
            {t('removeSelected')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleRemoveAll}
            disabled={orderedSelectedFields.length === 0}
            fullWidth
            startIcon={<KeyboardDoubleArrowLeft />}
          >
            {t('removeAll')}
          </Button>
        </Grid>

        {/* Selected Fields */}
        <Grid
          item
          xs={12}
          md={5}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.custom.gray[200],
              height: 400,
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: theme.palette.custom.gray[200],
                backgroundColor: theme.palette.custom.gray[50],
              }}
            >
              <Typography
                fontWeight="bold"
              >
                {t('selectedFields')}
              </Typography>
            </Box>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selected-fields">
                {(provided, snapshot) => (
                  <List
                    dense
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      backgroundColor: snapshot.isDraggingOver
                        ? theme.palette.custom.gray[50]
                        : 'transparent',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {orderedSelectedFields.map((metricId, index) => {
                      const metric = columnMetrics?.find((m) => m.id === metricId);
                      if (!metric) return null;

                      return (
                        <Draggable
                          key={metricId}
                          draggableId={String(metricId)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              disablePadding
                              sx={{
                                backgroundColor: snapshot.isDragging
                                  ? theme.palette.custom.gray[100]
                                  : 'white',
                                boxShadow: snapshot.isDragging
                                  ? '0 5px 10px rgba(0,0,0,0.15)'
                                  : 'none',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                  backgroundColor: theme.palette.custom.gray[50],
                                },
                              }}
                              secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleMoveUp(index)}
                                    disabled={index === 0}
                                  >
                                    <ArrowUpward fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleMoveDown(index)}
                                    disabled={index === orderedSelectedFields.length - 1}
                                  >
                                    <ArrowDownward fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <Box
                                {...provided.dragHandleProps}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'grab',
                                  '&:active': {
                                    cursor: 'grabbing',
                                  },
                                  mr: 1,
                                  ml: 1,
                                  color: theme.palette.custom.gray[400],
                                }}
                              >
                                <DragIndicator fontSize="small" />
                              </Box>
                              <ListItemButton
                                onClick={() => handleToggleSelected(metricId)}
                                dense
                                sx={{ flex: 1 }}
                              >
                                <Checkbox
                                  edge="start"
                                  checked={selectedFields.includes(metricId)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                                <ListItemText
                                  primary={metric.name}
                                  title={metric.description || metric.name || ''}
                                />
                              </ListItemButton>
                            </ListItem>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>
        </Grid>
      </Grid>

      <Typography
        sx={{
          mt: 2,
          display: 'block',
          fontSize: '0.75rem',
          color: theme.palette.custom.gray[600],
        }}
      >
        {t('dragToReorderFields') || 'Drag to reorder fields, or use the arrow buttons'}
      </Typography>
    </Box>
  );
};

export default FieldSelector;
